import type { ReactNode } from "react";

import { Box, Card, Flex, SegmentedControl, Text } from "@radix-ui/themes";

import {
  PORTFOLIO_LOOKBACKS,
  type PortfolioLookback,
} from "~/v3-poc/features/portfolio-history/portfolioHistoryTimeSettings";
import {
  directionColor,
  directionOf,
  formatCurrency,
  formatPercent,
  formatSignedCurrency,
} from "../overview.format";
import type { AccountCardPresentationProps as SharedAccountCardPresentationProps } from "../types";

const UNAVAILABLE = "—";

export type AccountCardPresentationProps = Omit<
  SharedAccountCardPresentationProps,
  "data" | "baseline" | "isLoading" | "hasError" | "theme"
> & {
  chart: ReactNode;
};

export const AccountCardPresentation = ({
  label,
  accountId,
  netLiq,
  change,
  changePercent,
  lookback,
  onLookbackChange,
  chart,
}: AccountCardPresentationProps) => {
  const direction = directionOf(change ?? 0);

  return (
    <Card size="2" className="flex-1">
      <Flex direction="column" gap="3" height="100%">
        <Flex direction="column" gap="1">
          <Text size="1" color="gray" weight="medium">
            Account
          </Text>
          <Text size="4" weight="bold" truncate>
            {label}
          </Text>
          <Text size="1" color="gray" className="atd-numeric">
            {accountId}
          </Text>
        </Flex>

        <Flex direction="column" gap="1">
          <Text size="1" color="gray">
            Portfolio balance
          </Text>
          <Text size="6" weight="bold" className="atd-numeric">
            {netLiq === undefined ? UNAVAILABLE : formatCurrency(netLiq)}
          </Text>
          <Text asChild size="1" color={directionColor(direction)}>
            <span className="atd-numeric">
              {change === undefined
                ? `${UNAVAILABLE} · ${lookback}`
                : `${formatSignedCurrency(change)}${
                    changePercent === undefined
                      ? ""
                      : ` (${formatPercent(changePercent)})`
                  } · ${lookback}`}
            </span>
          </Text>
        </Flex>

        <Box>{chart}</Box>

        <SegmentedControl.Root
          size="1"
          value={lookback}
          onValueChange={(value) =>
            value && onLookbackChange(value as PortfolioLookback)
          }
          aria-label="Chart range"
          className="mt-auto"
        >
          {PORTFOLIO_LOOKBACKS.map((option) => (
            <SegmentedControl.Item key={option.value} value={option.value}>
              {option.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </Flex>
    </Card>
  );
};
